/**
 * Los cálculos de la pantalla de proveedores. Sin React y sin fetch.
 *
 * LAS DOS MONEDAS NO SE SUMAN. Deberle US$ 600 y $ 300.000 son dos deudas que
 * se saldan por separado, y juntarlas en un número obligaría a elegir una
 * cotización que nadie pactó. El total del pie son dos números, y el tercero
 * —`ars_hoy`— es una foto para ordenar, no una deuda.
 */
import { formatear, formatearNumero, redondear, type Moneda } from "@tora/shared";
import { aNumero, type Caja, type Numerico, type SaldoProveedor } from "@/lib/tipos";

export type { SaldoProveedor };

/** La misma tolerancia que usa el FIFO en la base. */
const CENTAVO = 0.009;

/** Un renglón del extracto (`v_extracto_proveedores`). */
export interface MovimientoProveedor {
  id: number;
  proveedor_id: number;
  fecha: string;
  tipo: "COMPRA" | "PAGO" | "NC" | "AJUSTE+" | "AJUSTE-" | "SALDO_INICIAL";
  referencia: string | null;
  detalle: string | null;
  compra_id: string | null;
  moneda: Moneda;
  cotizacion: Numerico;
  deuda: Numerico;
  pago: Numerico;
  saldo_acumulado_ars: Numerico;
  saldo_acumulado_usd: Numerico;
  observaciones: string | null;
  /** De qué caja salió un pago, y cuánto en su moneda (0121_9c). Null si no es un pago con caja. */
  medios?: ReadonlyArray<{ caja: string; moneda: Moneda; importe: Numerico }> | null;
}

/**
 * Con qué se pagó: «desde Efectivo ARS, $ 155.000,00 a dólar 1.550,00». El
 * renglón del pago decía solo «US$ 100,00» y no se veía de qué caja salió la
 * plata ni a qué dólar se convirtió. El dólar va solo si la caja es de otra
 * moneda que la deuda: si no, no se convirtió nada.
 */
export function detalleDelPago(m: MovimientoProveedor): string | null {
  if (m.tipo !== "PAGO" || !m.medios || m.medios.length === 0) return null;
  const partes = m.medios.map((medio) =>
    medio.moneda === m.moneda
      ? `desde ${medio.caja}`
      : `desde ${medio.caja}, ${formatear(aNumero(medio.importe), medio.moneda)}`,
  );
  const convertido = m.medios.some((medio) => medio.moneda !== m.moneda);
  const dolar = convertido ? ` a dólar ${formatearNumero(aNumero(m.cotizacion), 2)}` : "";
  return `${partes.join(" y ")}${dolar}`;
}

/** Una deuda abierta (`v_deudas_proveedor`). */
export interface DeudaProveedor {
  movimiento_id: number;
  proveedor_id: number;
  fecha: string;
  tipo: string;
  referencia: string | null;
  compra_id: string | null;
  moneda: Moneda;
  cotizacion: Numerico;
  deuda: Numerico;
  imputado: Numerico;
  pendiente: Numerico;
  dias: Numerico;
}

/** Si esta fila tiene algo que mirar: deuda, o plata a favor. */
export function hayDeuda(f: Pick<SaldoProveedor, "saldo_ars" | "saldo_usd">): boolean {
  return Math.abs(aNumero(f.saldo_ars)) > CENTAVO || Math.abs(aNumero(f.saldo_usd)) > CENTAVO;
}

/** El total del pie, una moneda por su lado. */
export function totalDeuda(filas: ReadonlyArray<Pick<SaldoProveedor, "saldo_ars" | "saldo_usd">>): {
  ars: number;
  usd: number;
} {
  const t = filas.reduce(
    (acc, f) => ({
      ars: acc.ars + aNumero(f.saldo_ars),
      usd: acc.usd + aNumero(f.saldo_usd),
    }),
    { ars: 0, usd: 0 },
  );
  return { ars: Math.round(t.ars * 100) / 100, usd: Math.round(t.usd * 100) / 100 };
}

/**
 * En qué monedas debe este proveedor.
 *
 * Lo usa el diálogo de pago: con una sola moneda no hay nada que preguntar, con
 * dos hay que saber contra cuál va el pago antes de elegir con qué se paga.
 */
export function monedasQueDebe(
  f: Pick<SaldoProveedor, "saldo_ars" | "saldo_usd">,
): ReadonlyArray<Moneda> {
  const m: Moneda[] = [];
  if (aNumero(f.saldo_ars) > CENTAVO) m.push("ARS");
  if (aNumero(f.saldo_usd) > CENTAVO) m.push("USD");
  return m;
}

/** El saldo de una moneda puntual. */
export function saldoEn(f: Pick<SaldoProveedor, "saldo_ars" | "saldo_usd">, m: Moneda): number {
  return aNumero(m === "USD" ? f.saldo_usd : f.saldo_ars);
}

/**
 * Cómo se lee la antigüedad en la lista.
 *
 * Es la edad de la deuda pendiente más vieja, que es el número que no se puede
 * maquillar con un pago chico. Null cuando no se debe nada.
 */
/** Los días de la deuda abierta más vieja, en cada moneda (null si no debe en esa). */
export function antiguedadPorMoneda(
  abiertas: ReadonlyArray<Pick<DeudaProveedor, "moneda" | "dias" | "pendiente">>,
): Record<Moneda, number | null> {
  const res: Record<Moneda, number | null> = { ARS: null, USD: null };
  for (const d of abiertas) {
    if (aNumero(d.pendiente) <= CENTAVO) continue;
    const dias = aNumero(d.dias);
    const actual = res[d.moneda];
    res[d.moneda] = actual === null ? dias : Math.max(actual, dias);
  }
  return res;
}

export function textoAntiguedad(dias: Numerico | null): string | null {
  if (dias === null || dias === undefined) return null;
  const d = aNumero(dias);
  if (d <= 0) return "hoy";
  if (d === 1) return "1 día";
  if (d < 31) return `${d} días`;
  const meses = Math.floor(d / 30);
  return meses === 1 ? "1 mes" : `${meses} meses`;
}

/**
 * La antigüedad como frase: «hace 3 días», y «de hoy» cuando es de hoy.
 *
 * Las pantallas escribian «hace {textoAntiguedad}», que con una deuda del dia
 * decia «hace hoy».
 */
export function haceCuanto(dias: Numerico | null): string {
  const texto = textoAntiguedad(dias);
  if (texto === null) return "—";
  return texto === "hoy" ? "de hoy" : `hace ${texto}`;
}

/** Cómo se lee cada tipo de movimiento de la cuenta del proveedor. */
export const ETIQUETA_TIPO_PROVEEDOR: Record<MovimientoProveedor["tipo"], string> = {
  COMPRA: "Compra",
  PAGO: "Pago",
  NC: "Nota de crédito",
  "AJUSTE+": "Ajuste a favor",
  "AJUSTE-": "Ajuste en contra",
  SALDO_INICIAL: "Saldo inicial",
};

/**
 * El nombre de una deuda abierta: su referencia (el numero de factura), o el
 * tipo en castellano. Sin referencia se mostraba el codigo pelado, «AJUSTE-».
 */
export function nombreDeuda(d: Pick<DeudaProveedor, "referencia" | "tipo">): string {
  return d.referencia ?? ETIQUETA_TIPO_PROVEEDOR[d.tipo as MovimientoProveedor["tipo"]] ?? d.tipo;
}

// ─────────────────────────────────────────────────────────────────────────────
// Cómo fluctuó la deuda
// ─────────────────────────────────────────────────────────────────────────────

/** La hora del local. Explicita: no depende de la zona de la compu que mira. */
const ZONA_LOCAL = "America/Argentina/Buenos_Aires";

/** "2026-09-29" en la hora del local. Una fecha sola (sin hora) queda como vino. */
function diaLocal(fechaIso: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(fechaIso)) return fechaIso;
  const d = new Date(fechaIso);
  if (Number.isNaN(d.getTime())) return fechaIso.slice(0, 10);
  // en-CA escribe AAAA-MM-DD.
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: ZONA_LOCAL,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);
}

/**
 * El mes de un timestamp ISO, como "2026-09", en la hora del local.
 *
 * Antes se recortaba el string suponiendo que venia en hora local, pero el
 * backend lo manda en UTC: un pago del 30/09 a las 22:30 (01:30Z del 1/10)
 * caia en octubre. Se pasa a la hora de Buenos Aires y recien ahi se recorta.
 */
export function mesDe(fechaIso: string): string {
  return diaLocal(fechaIso).slice(0, 7);
}

/**
 * La fecha de un movimiento como la lee el local: "29/09/2026".
 *
 * El extracto mostraba `fecha.slice(0, 10)`: la fecha en UTC y en formato ISO.
 * Lo que pasaba despues de las 21:00 caia en el dia siguiente, y a fin de mes
 * en el mes siguiente.
 */
export function fechaDeMovimiento(fechaIso: string): string {
  const [anio, mes, dia] = diaLocal(fechaIso).split("-");
  return anio && mes && dia ? `${dia}/${mes}/${anio}` : fechaIso;
}

/** Una fila del resumen mensual, en UNA moneda. */
export interface MesDeCuenta {
  /** "2026-09". Se ordena como texto porque el formato lo permite. */
  mes: string;
  compras: number;
  pagos: number;
  /** Saldo al cierre del mes, acumulado desde el primer movimiento. */
  cierre: number;
}

/**
 * Compras, pagos y saldo al cierre, mes por mes.
 *
 * ES EL BLOQUE QUE CONTESTA "CÓMO FLUCTUÓ", y el que reemplaza al gráfico que se
 * descartó. La pregunta real no es la forma de la curva: es «¿la deuda con este
 * tipo viene creciendo?», y tres números por mes la contestan igual de rápido
 * sin meterle una librería de gráficos al proyecto.
 *
 * EL CIERRE ACUMULA DESDE EL PRINCIPIO, no desde el primer mes con actividad: el
 * cierre de agosto tiene que incluir lo que venía de julio o la columna miente.
 * Los meses sin movimientos no generan fila —doce filas de ceros no dicen nada—
 * pero el mes siguiente arrastra el acumulado igual.
 *
 * UNA MONEDA POR LLAMADA. Un proveedor puede deber en las dos y no se suman
 * nunca; la pantalla llama dos veces y muestra dos tablas.
 */
export function resumenPorMes(
  movimientos: ReadonlyArray<MovimientoProveedor>,
  moneda: Moneda,
): MesDeCuenta[] {
  const porMes = new Map<string, { compras: number; pagos: number }>();

  for (const m of movimientos) {
    if (m.moneda !== moneda) continue;
    const mes = mesDe(m.fecha);
    const acc = porMes.get(mes) ?? { compras: 0, pagos: 0 };
    acc.compras += aNumero(m.deuda);
    acc.pagos += aNumero(m.pago);
    porMes.set(mes, acc);
  }

  let acumulado = 0;
  return [...porMes.keys()].sort().map((mes) => {
    const { compras, pagos } = porMes.get(mes)!;
    acumulado = redondear(acumulado + compras - pagos);
    return {
      mes,
      compras: redondear(compras),
      pagos: redondear(pagos),
      cierre: acumulado,
    };
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// De dónde viene: el estado de cada factura
// ─────────────────────────────────────────────────────────────────────────────

/** Cómo quedó una deuda del extracto. */
export type EstadoPagoDeuda =
  | { clase: "PAGADA" }
  | { clase: "PARCIAL"; pagado: number; total: number }
  | { clase: "ABIERTA"; total: number };

/**
 * El estado de pago de un renglón del extracto.
 *
 * VA EN EL RENGLÓN DE LA FACTURA Y NO EN EL DEL PAGO, porque así llega la
 * pregunta: el proveedor llama por la factura 42, no por el pago del martes. El
 * rastro está en `imputaciones_proveedor` desde 0062 y hasta ahora ninguna
 * pantalla lo leía: el FIFO decidía y no se veía qué había decidido.
 *
 * Devuelve `null` para lo que no es una deuda —un pago, una nota de crédito—:
 * esos renglones no llevan estado.
 */
export function estadoDePago(
  movimientoId: number,
  deudas: ReadonlyArray<DeudaProveedor>,
): EstadoPagoDeuda | null {
  const d = deudas.find((x) => x.movimiento_id === movimientoId);
  if (!d) return null;

  const total = aNumero(d.deuda);
  const pagado = aNumero(d.imputado);

  // La misma tolerancia que el FIFO: un centavo imposible de pagar no deja una
  // factura figurando como abierta para siempre.
  if (aNumero(d.pendiente) <= CENTAVO) return { clase: "PAGADA" };
  if (pagado > CENTAVO) return { clase: "PARCIAL", pagado, total };
  return { clase: "ABIERTA", total };
}

// ─────────────────────────────────────────────────────────────────────────────
// La caja de donde sale el pago
// ─────────────────────────────────────────────────────────────────────────────

export interface CajaEnNegativo {
  nombre: string;
  moneda: Moneda;
  saldo: number;
  queda: number;
}

/**
 * Las cajas que este pago deja en negativo.
 *
 * Se registraba un pago de US$ 55.014 desde una caja con US$ 6.877 sin ningun
 * aviso. No se bloquea (hay cajas todavia sin su saldo inicial cargado), pero
 * se dice. Una cuenta que no esta entre los saldos no se avisa: no se sabe.
 */
export function cajasEnNegativo(
  pagos: ReadonlyArray<{ cuenta_id: number; importe: number | null }>,
  cajas: ReadonlyArray<Pick<Caja, "id" | "nombre" | "moneda" | "saldo">>,
): CajaEnNegativo[] {
  const porCuenta = new Map<number, number>();
  for (const p of pagos) {
    if (p.importe === null || p.importe <= 0) continue;
    porCuenta.set(p.cuenta_id, (porCuenta.get(p.cuenta_id) ?? 0) + p.importe);
  }
  const res: CajaEnNegativo[] = [];
  for (const [cuentaId, sale] of porCuenta) {
    const caja = cajas.find((c) => c.id === cuentaId);
    if (!caja) continue;
    const saldo = aNumero(caja.saldo);
    const queda = redondear(saldo - sale);
    if (queda < -CENTAVO) res.push({ nombre: caja.nombre, moneda: caja.moneda, saldo, queda });
  }
  return res;
}
